"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { LiveCount, LiveStatus } from "@/lib/servers";

// Client-side live player counts. A single provider polls `/api/status` and
// shares the result via context; components read it with `useLiveCount(slug)`
// and fall back to their static values until (and if) live data arrives.

const POLL_INTERVAL = 30_000; // 30s

const LiveContext = createContext<LiveStatus | null>(null);

export function useLiveStatus(): LiveStatus | null {
  return useContext(LiveContext);
}

export function useLiveCount(slug: string): LiveCount | null {
  return useContext(LiveContext)?.[slug] ?? null;
}

export default function LiveCountsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [status, setStatus] = useState<LiveStatus | null>(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const res = await fetch("/api/status", { cache: "no-store" });
        if (!res.ok) return;
        const data = (await res.json()) as LiveStatus;
        if (active) setStatus(data);
      } catch {
        // Network hiccup — keep the last known (or static fallback) values.
      }
    };

    load();
    const id = setInterval(load, POLL_INTERVAL);
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      active = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return <LiveContext.Provider value={status}>{children}</LiveContext.Provider>;
}
